import { useState } from 'react';
import { Clock, Users, Calendar, ChevronDown, AlertCircle, CheckCircle2, Info } from 'lucide-react';
import { Card, CardHeader, CardContent } from '../core/Card';
import { Button } from '../core/Button';

export function TimeOverviewView() {
  const [selectedFilter, setSelectedFilter] = useState<'all' | 'missing' | 'complete'>('all');
  const [selectedWeek, setSelectedWeek] = useState('current');

  // Mock data for time entries overview
  const timeData = [
    {
      id: 1,
      employee: 'Müller, Thomas',
      weekHours: 40.0,
      expectedHours: 40.0,
      status: 'complete',
      missingDays: 0,
      lastEntry: '08.01.2026',
    },
    {
      id: 2,
      employee: 'Schmidt, Anna',
      weekHours: 32.5,
      expectedHours: 40.0,
      status: 'sick',
      missingDays: 0,
      lastEntry: '07.01.2026',
      note: 'Krankmeldung ab 08.01.',
    },
    {
      id: 3,
      employee: 'Weber, Klaus',
      weekHours: 38.0,
      expectedHours: 40.0,
      status: 'incomplete',
      missingDays: 1,
      lastEntry: '07.01.2026',
    },
    {
      id: 4,
      employee: 'Meyer, Lisa',
      weekHours: 24.0,
      expectedHours: 40.0,
      status: 'missing',
      missingDays: 3,
      lastEntry: '03.01.2026',
    },
    {
      id: 5,
      employee: 'Fischer, Marc',
      weekHours: 40.0,
      expectedHours: 40.0,
      status: 'complete',
      missingDays: 0,
      lastEntry: '08.01.2026',
    },
    {
      id: 6,
      employee: 'Wagner, Petra',
      weekHours: 40.0,
      expectedHours: 40.0,
      status: 'complete',
      missingDays: 0,
      lastEntry: '08.01.2026',
    },
    {
      id: 7,
      employee: 'Becker, Stefan',
      weekHours: 36.0,
      expectedHours: 40.0,
      status: 'incomplete',
      missingDays: 1,
      lastEntry: '06.01.2026',
    },
    {
      id: 8,
      employee: 'Schulz, Maria',
      weekHours: 40.0,
      expectedHours: 40.0,
      status: 'complete',
      missingDays: 0,
      lastEntry: '08.01.2026',
    },
  ];

  const getFilteredData = () => {
    switch (selectedFilter) {
      case 'missing':
        return timeData.filter(d => d.status === 'missing' || d.status === 'incomplete');
      case 'complete':
        return timeData.filter(d => d.status === 'complete');
      default:
        return timeData;
    }
  };

  const filteredData = getFilteredData();

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'complete':
        return (
          <div className="flex items-center gap-1 px-2 py-1 rounded-md text-[12px] font-medium bg-green-50 text-green-700">
            <CheckCircle2 className="size-3" />
            Vollständig
          </div>
        );
      case 'sick':
        return (
          <div className="flex items-center gap-1 px-2 py-1 rounded-md text-[12px] font-medium bg-blue-50 text-blue-700">
            <Info className="size-3" />
            Krank
          </div>
        );
      case 'incomplete':
        return (
          <div className="flex items-center gap-1 px-2 py-1 rounded-md text-[12px] font-medium bg-orange-50 text-orange-700">
            <AlertCircle className="size-3" />
            Unvollständig
          </div>
        );
      case 'missing':
        return (
          <div className="flex items-center gap-1 px-2 py-1 rounded-md text-[12px] font-medium bg-red-50 text-red-700">
            <AlertCircle className="size-3" />
            Fehlend
          </div>
        );
      default:
        return null;
    }
  };

  const summaryStats = {
    totalEmployees: timeData.length,
    complete: timeData.filter(d => d.status === 'complete').length,
    incomplete: timeData.filter(d => d.status === 'incomplete' || d.status === 'missing').length,
    totalHours: timeData.reduce((sum, d) => sum + d.weekHours, 0),
    averageHours: (timeData.reduce((sum, d) => sum + d.weekHours, 0) / timeData.length).toFixed(1),
  };

  return (
    <div className="bg-[#f5f5f5] min-h-screen p-4 md:p-8">
      <div className="max-w-[1400px] mx-auto">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6">
          <Card>
            <CardContent>
              <div className="flex items-center gap-3">
                <div className="size-10 bg-blue-50 rounded-lg flex items-center justify-center">
                  <Users className="size-5 text-blue-700" />
                </div>
                <div className="flex-1">
                  <p className="text-[#6b6b6b] text-[13px] mb-1">Mitarbeiter</p>
                  <p className="text-[#1c1c1c] text-[24px] font-medium">
                    {summaryStats.totalEmployees}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <div className="flex items-center gap-3">
                <div className="size-10 bg-green-50 rounded-lg flex items-center justify-center">
                  <CheckCircle2 className="size-5 text-green-700" />
                </div>
                <div className="flex-1">
                  <p className="text-[#6b6b6b] text-[13px] mb-1">Vollständig</p>
                  <p className="text-[#1c1c1c] text-[24px] font-medium">
                    {summaryStats.complete}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <div className="flex items-center gap-3">
                <div className="size-10 bg-orange-50 rounded-lg flex items-center justify-center">
                  <AlertCircle className="size-5 text-orange-700" />
                </div>
                <div className="flex-1">
                  <p className="text-[#6b6b6b] text-[13px] mb-1">Unvollständig</p>
                  <p className="text-[#1c1c1c] text-[24px] font-medium">
                    {summaryStats.incomplete}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <div className="flex items-center gap-3">
                <div className="size-10 bg-purple-50 rounded-lg flex items-center justify-center">
                  <Clock className="size-5 text-purple-700" />
                </div>
                <div className="flex-1">
                  <p className="text-[#6b6b6b] text-[13px] mb-1">Ø Stunden/MA</p>
                  <p className="text-[#1c1c1c] text-[24px] font-medium">
                    {summaryStats.averageHours}h
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters and Table */}
        <Card>
          <CardHeader
            title="Zeiterfassung Übersicht"
            subtitle="Wochenübersicht aller Mitarbeiter (KW 2, 2026)"
            borderBottom
            action={
              <div className="flex items-center gap-3">
                <select
                  value={selectedWeek}
                  onChange={(e) => setSelectedWeek(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-[14px] bg-white"
                >
                  <option value="current">Aktuelle Woche</option>
                  <option value="last">Letzte Woche</option>
                  <option value="custom">Zeitraum wählen</option>
                </select>
              </div>
            }
          />

          {/* Filter Tabs */}
          <div className="border-b border-gray-200 px-6 pt-4">
            <div className="flex gap-2">
              <button
                onClick={() => setSelectedFilter('all')}
                className={`px-4 py-2 text-[14px] font-medium rounded-t-lg transition-colors ${
                  selectedFilter === 'all'
                    ? 'bg-white text-[#0084c7] border-b-2 border-[#0084c7]'
                    : 'text-[#6b6b6b] hover:text-[#1c1c1c]'
                }`}
              >
                Alle ({timeData.length})
              </button>
              <button
                onClick={() => setSelectedFilter('missing')}
                className={`px-4 py-2 text-[14px] font-medium rounded-t-lg transition-colors ${
                  selectedFilter === 'missing'
                    ? 'bg-white text-[#0084c7] border-b-2 border-[#0084c7]'
                    : 'text-[#6b6b6b] hover:text-[#1c1c1c]'
                }`}
              >
                Unvollständig ({summaryStats.incomplete})
              </button>
              <button
                onClick={() => setSelectedFilter('complete')}
                className={`px-4 py-2 text-[14px] font-medium rounded-t-lg transition-colors ${
                  selectedFilter === 'complete'
                    ? 'bg-white text-[#0084c7] border-b-2 border-[#0084c7]'
                    : 'text-[#6b6b6b] hover:text-[#1c1c1c]'
                }`}
              >
                Vollständig ({summaryStats.complete})
              </button>
            </div>
          </div>

          <CardContent>
            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-[#6b6b6b] text-[13px] font-medium">
                      Mitarbeiter
                    </th>
                    <th className="text-left py-3 px-4 text-[#6b6b6b] text-[13px] font-medium">
                      Status
                    </th>
                    <th className="text-right py-3 px-4 text-[#6b6b6b] text-[13px] font-medium">
                      Ist-Stunden
                    </th>
                    <th className="text-right py-3 px-4 text-[#6b6b6b] text-[13px] font-medium">
                      Soll-Stunden
                    </th>
                    <th className="text-center py-3 px-4 text-[#6b6b6b] text-[13px] font-medium">
                      Differenz
                    </th>
                    <th className="text-left py-3 px-4 text-[#6b6b6b] text-[13px] font-medium">
                      Letzter Eintrag
                    </th>
                    <th className="text-right py-3 px-4 text-[#6b6b6b] text-[13px] font-medium">
                      Aktion
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.map((entry) => {
                    const diff = entry.weekHours - entry.expectedHours;
                    return (
                      <tr key={entry.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-4 px-4">
                          <div>
                            <p className="text-[#1c1c1c] text-[14px] font-medium">
                              {entry.employee}
                            </p>
                            {entry.note && (
                              <p className="text-[#6b6b6b] text-[12px] mt-0.5">
                                {entry.note}
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          {getStatusBadge(entry.status)}
                        </td>
                        <td className="py-4 px-4 text-right">
                          <p className="text-[#1c1c1c] text-[14px] font-medium">
                            {entry.weekHours.toFixed(1)}h
                          </p>
                        </td>
                        <td className="py-4 px-4 text-right">
                          <p className="text-[#6b6b6b] text-[14px]">
                            {entry.expectedHours.toFixed(1)}h
                          </p>
                        </td>
                        <td className="py-4 px-4 text-center">
                          <span className={`inline-flex px-2 py-1 rounded text-[13px] font-medium ${
                            diff === 0 
                              ? 'bg-gray-100 text-gray-700'
                              : diff > 0
                              ? 'bg-green-50 text-green-700'
                              : 'bg-red-50 text-red-700'
                          }`}>
                            {diff > 0 ? '+' : ''}{diff.toFixed(1)}h
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <p className="text-[#6b6b6b] text-[13px]">
                            {entry.lastEntry}
                          </p>
                          {entry.missingDays > 0 && (
                            <p className="text-red-600 text-[12px] mt-0.5">
                              {entry.missingDays} {entry.missingDays === 1 ? 'Tag' : 'Tage'} fehlend
                            </p>
                          )}
                        </td>
                        <td className="py-4 px-4 text-right">
                          <Button
                            variant="outline"
                            size="sm"
                          >
                            Details
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {filteredData.length === 0 && (
              <div className="text-center py-12">
                <p className="text-[#6b6b6b] text-[14px]">
                  Keine Einträge für die ausgewählten Filter
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info Box */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-[10px] p-4">
          <div className="flex items-start gap-3">
            <div className="size-10 bg-blue-100 rounded-lg flex items-center justify-center shrink-0">
              <Info className="size-5 text-blue-700" />
            </div>
            <div className="flex-1">
              <h3 className="text-[#1c1c1c] text-[16px] font-medium mb-1">
                Zeitübersicht für Administratoren
              </h3>
              <p className="text-[#6b6b6b] text-[14px]">
                Diese Ansicht dient ausschließlich der Übersicht und Kontrolle. Zeiteinträge können hier nicht erfasst oder bearbeitet werden. Für die eigene Zeiterfassung wechseln Sie bitte in die Benutzeransicht.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
